import { Box, Typography, Button, Checkbox, Stack } from '@mui/material';
import { useState } from 'react';

import WhyImportantLink from '../../components/WhyImportantLink';
import { ACCOUNTING_SETUP_HELP_CONTEXT } from '../../hooks/useOpenAccountingSetupHelp';

import BankLogo from 'modules/HubV2/Bookkeeping/components/Banks2/BankList/Bank/BankLogo';
import UploadedFileRow from 'modules/common/components/UploadedFileRow';

export interface DocumentType {
    id: string;
    name: string;
    description: string;
    required: boolean;
    documentType: string; // Backend document type
    bankAccountId?: string; // For bank statements
    bankAccountName?: string; // For display
    institutionName?: string; // For bank logo fetching
    institutionId?: string; // Plaid institution ID for logo fetching
    mask?: string; // For bank statements
    twoSided?: boolean;
    isBackSide?: boolean;
}

interface DocumentItemProps {
    document: DocumentType;
    uploadedDocuments?: any[]; // Array of uploaded document objects
    isSkipped: boolean;
    onUpload: (documentId: string) => void;
    onSkip: (documentId: string, skipped: boolean) => void;
    onDeleteFile: (documentId: string, fileId: string) => void; // For file deletion
}

export default function DocumentItem({
    document,
    uploadedDocuments = [],
    isSkipped,
    onUpload,
    onSkip,
    onDeleteFile,
}: DocumentItemProps) {
    const [isChecked, setIsChecked] = useState(isSkipped);

    // Compute if document is uploaded based on uploadedDocuments array
    const isUploaded = !!(uploadedDocuments && uploadedDocuments.length > 0);

    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { checked } = event.target;
        setIsChecked(checked);
        onSkip(document.id, checked);
    };

    const handleUploadClick = () => {
        onUpload(document.id);
    };

    const getButtonText = () => {
        if (isUploaded) {
            return 'Add more';
        }
        return 'Upload';
    };

    const getCheckboxText = () => {
        return "I don't have these documents";
    };

    // Generic helper function to render uploaded files list
    const renderUploadedFilesList = () => {
        if (!isUploaded || !uploadedDocuments || uploadedDocuments.length === 0) {
            return null;
        }

        return (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {uploadedDocuments.map((file, index) => {
                    const fileObj = Array.isArray(file) ? file[0] : file;
                    return (
                        <UploadedFileRow
                            name={fileObj.file_name}
                            sizeKB={fileObj.file_size}
                            onDelete={() => onDeleteFile(document.id, fileObj.id)}
                            isFirst={index === 0}
                            isLast={index === uploadedDocuments.length - 1}
                        />
                    );
                })}
            </Box>
        );
    };

    // Check if this is a bank statement document
    const isBankStatement = document.bankAccountId;

    /**
     * The member's own question, for whichever document this card is asking for.
     *
     * One template rather than a per-type `switch`, so a document type added to
     * `getDocumentTypesFromAnswers` gets a working question without a second edit here. A `switch`
     * buys nothing either way: every branch would be this same sentence with the name spliced in,
     * and its `default` would have to invent one anyway.
     *
     * Derived from `document.name` rather than written out so a year-bearing label stays derived:
     * the card says "2025 Individual Tax Return" because `getDocumentTypeDisplayName` reads
     * `getCurrentTaxYear()`, and the question has to name that same year at the rollover (AP-2734).
     *
     * Phrased as a question, not as "Help with X document requirements" — AP-2448 found the request
     * shape makes the agent read two asks and split its answer between them.
     *
     * Coverage for some of these lives on the Decagon side rather than in a help-centre article
     * (vehicle, loan and prior-year GL/P&L have no article that explains why we collect them). That
     * is an AOP/knowledge concern, deliberately not a reason to route the question elsewhere.
     */
    const helpQuestion = `Why do you need my ${document.name}?`;

    if (isBankStatement) {
        // Bank statement layout matching Figma
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Bank account info and upload button row */}
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                    <Stack direction="row" spacing={2} alignItems="center">
                        {/* Bank Logo */}
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="center"
                            sx={{
                                width: 40,
                                height: 40,
                                '& img, & svg': { width: '100%', height: '100%', objectFit: 'contain' },
                            }}>
                            <BankLogo institutionId={document.institutionId || ''} width={40} height={40} />
                        </Stack>

                        {/* Account info */}
                        <Box>
                            <Typography variant="h5" color="text.primary">
                                {document.bankAccountName?.split(' (')[0] || document.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Credit • {document.mask}
                            </Typography>
                        </Box>
                    </Stack>

                    {/* Upload button */}
                    <Button
                        variant={isUploaded ? 'outlined' : 'contained'}
                        color="secondary"
                        onClick={handleUploadClick}
                        sx={{ whiteSpace: 'nowrap', minWidth: 101, height: 40 }}>
                        {getButtonText()}
                    </Button>
                </Stack>

                {/* I don't have these documents checkbox - only show when no file is uploaded */}
                {!isUploaded && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Checkbox
                            checked={isChecked}
                            color="secondary"
                            onChange={handleCheckboxChange}
                            size="small"
                            sx={{ p: 0 }}
                        />
                        <Typography variant="body2" color="text.primary">
                            {getCheckboxText()}
                        </Typography>
                    </Box>
                )}

                {/* Uploaded files list */}
                {renderUploadedFilesList()}
            </Box>
        );
    }

    // Business document layout matching Figma
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Document title and upload button row */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 3 }}>
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Typography variant="h5" color="text.primary">
                        {document.name}
                    </Typography>
                </Box>

                {/* Upload button */}
                <Button
                    onClick={handleUploadClick}
                    variant={isUploaded ? 'outlined' : 'contained'}
                    color="onyx"
                    disabled={isChecked}
                    sx={{
                        width: '101px',
                        height: '40px',
                        whiteSpace: 'nowrap',
                    }}>
                    {getButtonText()}
                </Button>
            </Box>

            {/* What's this? link.
                `requiresSetupAgent` stays off: these links predate AP-2448 and have a gate-off home
                already, so flipping the gate off returns them to the generic widget rather than
                removing them. */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <WhyImportantLink
                    context={ACCOUNTING_SETUP_HELP_CONTEXT.uploadDocuments}
                    title="What's this?"
                    helpQuestion={helpQuestion}
                />
            </Box>

            {/* I don't have these documents checkbox - only show when no file is uploaded */}
            {!isUploaded && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Checkbox
                        checked={isChecked}
                        color="secondary"
                        onChange={handleCheckboxChange}
                        size="small"
                        sx={{ p: 0 }}
                    />
                    <Typography variant="body2" color="text.primary">
                        {getCheckboxText()}
                    </Typography>
                </Box>
            )}

            {/* Uploaded files list */}
            {renderUploadedFilesList()}
        </Box>
    );
}
